import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Grid2 as Grid, Typography, Box } from '@mui/material';
import useValidationSchema from './_form';
import { IPatientFormValues, IPatientFormComponent } from './_types';
import { Papeer } from 'components/Papeer/Papeer';
import FormInput from 'components/Form/Input/Input';
import FormSwitch from 'components/Form/Switch/Switch';
import FormSelect from 'components/Form/Select/Select';
import FormDatePicker from 'components/Form/Date/Date';
import { EntityButtons } from 'components/Form/EntityButtons/EntityButtons';
import Header from 'components/Header/Header';
import SharedContactSections from 'modules/Administration/PatientRegistry/SharedContactSections';

const PatientFormComponent: React.FC<IPatientFormComponent> = ({
  isCreating,
  defaultValues,
  onSubmit,
  onSaveAndOpen,
  linkBack,
  compactMode = false,
  title,
}) => {
  const { t } = useTranslation('Patients');

  const { PatientFormSchema, genderItems } = useValidationSchema(t);

  const methods = useForm<IPatientFormValues>({
    resolver: yupResolver(PatientFormSchema),
    defaultValues,
  });

  const { control, handleSubmit, watch } = methods;
  const deceased = watch('deceased');

  const {
    fields: identifierFields,
    append: appendIdentifier,
    remove: removeIdentifier,
  } = useFieldArray({ control, name: 'identifier' });

  const {
    fields: telecomFields,
    append: appendTelecom,
    remove: removeTelecom,
  } = useFieldArray({ control, name: 'telecom' });

  const {
    fields: addressFields,
    append: appendAddress,
    remove: removeAddress,
  } = useFieldArray({ control, name: 'address' });

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Header title={title} backUrl={linkBack} />

        <Papeer bottomMargin={true}>
          <Grid container spacing={compactMode ? 1 : 2}>
            {/* Basic Information */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6">{t('basicInfo')}</Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <FormInput name="lastName" label={t('lastName')} required />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <FormInput name="firstName" label={t('firstName')} required />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ pt: 1 }}>
                <FormSwitch name="active" label={t('active')} />
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <FormDatePicker name="birthDate" label={t('birthDateShort')} />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <FormInput name="birthNumber" label={t('birthNumber')} required />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <FormSelect name="gender" label={t('gender')} items={genderItems} />
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <Box sx={{ pt: 1 }}>
                <FormSwitch name="deceased" label={t('deceased')} />
              </Box>
            </Grid>

            {deceased && (
              <Grid size={{ xs: 12, md: 2 }}>
                <FormDatePicker name="deceasedDate" label={t('deceasedDate')} />
              </Grid>
            )}

            <SharedContactSections
              control={control}
              compactMode={compactMode}
              identifierInputMode="patient"
              identifierFields={identifierFields}
              appendIdentifier={appendIdentifier}
              removeIdentifier={removeIdentifier}
              telecomFields={telecomFields}
              appendTelecom={appendTelecom}
              removeTelecom={removeTelecom}
              addressFields={addressFields}
              appendAddress={appendAddress}
              removeAddress={removeAddress}
              allowedContactSystems={['phone', 'email']}
              fixedContactUse="home"
              fixedAddressUse="home"
              fixedAddressType="both"
            />
          </Grid>
        </Papeer>

        <Papeer>
          <EntityButtons
            linkBack={linkBack}
            onSaveAndOpen={onSaveAndOpen ? () => handleSubmit(onSaveAndOpen)() : undefined}
          />
        </Papeer>
      </form>
    </FormProvider>
  );
};

export default PatientFormComponent;
